import styles from './FaqModule.module.scss';

import cx from 'classnames';
import type { ReferencedEntry } from 'contentful';
import React, { FC, HTMLAttributes, useState } from 'react';

import { Faq, FaqModule as FaqModuleType, FaqVariantOptions } from 'api/contentful/contentTypes.cloverdotcom';
import { FaqButtonAccordion } from 'components/Accordion/FaqButtonAccordion/FaqButtonAccordion';
import type { IAccordionItem } from 'components/Accordion/models';
import { FadeToastUp } from 'components/Animation/FadeToastUp/FadeToastUp';
import { Grid } from 'components/Grid/Grid';
import { GridContainer } from 'components/GridContainer/GridContainer';
import { RichBlurb } from 'components/RichBlurb/RichBlurb';
import { TextWithArrow } from 'components/TextWithArrow/TextWithArrow';
import { TextV2 } from 'components/TextV2/TextV2';
import { flattenContentfulData, FlatEntry } from 'services/cms/flattenContentful';
import type { EntriesV2 } from 'services/cms/typesUtil';
import { componentKeyV2 } from 'utils/ComponentKeyV2';
import { BooleanFilter } from 'utils/TypeUtils';

interface IFaqModuleProps extends HTMLAttributes<HTMLElement> {
  entry?: FlatEntry<FaqModuleType>;
}

const FaqModule: FC<IFaqModuleProps> = ({ entry, className, style = {}, ...props }) => {
  const {
    backgroundColor,
    items = [],
    title,
    titleColor,
    variant = FaqVariantOptions.CIRCLE_ICON,
    limitEntryCount,
    viewMoreLabel,
    viewLessLabel,
    headingTagName = 'h2',
  } = entry || {};

  const isArrowIcon = variant === FaqVariantOptions.ARROW_ICON;
  const isDisplayViewMore = limitEntryCount && isArrowIcon && (items || [])?.length > limitEntryCount;
  const [isShowAll, setIsShowAll] = useState(false);

  if (!entry) return null;

  const TitleContainer = () => (
    <FadeToastUp>
      <GridContainer justifyContent="center">
        <Grid item xs={12} lg={10} xl={12} md>
          <TextV2
            hasId
            isMarkdown
            value={title}
            entry={{ ...entry, field: 'title' }}
            tagName={headingTagName}
            variant="h3"
            className={styles.faqModuleTitle}
            style={{ color: titleColor }}
          />
        </Grid>
      </GridContainer>
    </FadeToastUp>
  );

  const getAccordionItems = (accordionItems: EntriesV2<FaqModuleType> = []): IAccordionItem[] => {
    return accordionItems.filter(BooleanFilter).map((item: FlatEntry<Faq>, index) => ({
      id: componentKeyV2(entry, index),
      title: (
        <TextV2
          tagName="h3"
          variant="h5"
          entry={{ ...item, field: 'question' }}
          value={item.question}
          className={cx(styles.faqModuleQuestion)}
        />
      ),
      body: item.richBlurb ? (
        <RichBlurb
          className={cx(styles.faqModuleAnswer, styles.richBlurb)}
          document={item.richBlurb}
          noMarginGap
        />
      ) : (
        <TextV2
          tagName="div"
          variant="body"
          entry={{ ...item, field: 'answer' }}
          isMarkdown
          value={item.answer}
          className={cx(styles.faqModuleAnswer)}
        />
      ),
    }));
  };

  return (
    <section
      className={cx(styles.faqModule, className, {
        [styles.variantArrowIcon]: isArrowIcon,
      })}
      style={{ backgroundColor: backgroundColor || style.backgroundColor }}
      {...props}
    >
      {title && <TitleContainer />}
      <GridContainer justifyContent="center">
        <Grid item xs={12} lg={10} xl={12} md>
          <FadeToastUp>
            <div className={cx({ [styles.variantArrowIconContainer]: isArrowIcon })}>
              <div
                className={cx(styles.faqModuleCells, {
                  [styles.entryLimitContainer]: isArrowIcon,
                  [styles.entryLimitExpanded]: isShowAll,
                })}
              >
                <FaqButtonAccordion
                  items={getAccordionItems(items)}
                  variant={isArrowIcon}
                  isDisplayViewMore={isDisplayViewMore || false}
                  isShowAll={isShowAll}
                  limitEntryCount={limitEntryCount || 0}
                />
              </div>
            </div>
          </FadeToastUp>
          {isDisplayViewMore === true && (
            <FadeToastUp>
              <button
                className={styles.viewMoreLink}
                type="button"
                onClick={() => setIsShowAll(prev => !prev)}
              >
                {!isShowAll ? viewMoreLabel : viewLessLabel}
                <TextWithArrow
                  className={cx(styles.arrow, isShowAll ? styles.arrowDown : styles.arrowUp)}
                  theme="light"
                  variant="caretSmall"
                />
              </button>
            </FadeToastUp>
          )}
        </Grid>
      </GridContainer>
    </section>
  );
};

FaqModule.displayName = 'FaqModule';

interface IFaqModuleWrapperProps extends HTMLAttributes<HTMLDivElement> {
  entry?: ReferencedEntry<FaqModuleType>;
}

const FaqModuleWrapper: FC<IFaqModuleWrapperProps> = ({ entry, ...props }) => (
  <FaqModule {...props} entry={flattenContentfulData(entry)} />
);

FaqModuleWrapper.displayName = 'FaqModuleWrapper';

export { FaqModule as _FaqModule, FaqModuleWrapper as FaqModule };

export default FaqModuleWrapper;
